import { FC, useEffect } from 'react';
import { TextField } from '../../components/TextField';
import { TextArea } from '../../components/TextArea';
import { createUseStyles } from 'react-jss';
import { CareMapState } from '../../caremap/CareMapState';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { IMedication } from '../../caremap/ICareMap';
import { MedicationFormState } from './MedicationFormState';

const useStyles = createUseStyles({
    form: {
        gap: 20,
        display: 'flex',
        flexDirection: 'column'
    }
});

export type IMedicationEditorProps = {
    id: string;
    reportValidity: (valid: boolean) => void;
    readOnly?: boolean;
};

export const MedicationEditor: FC<IMedicationEditorProps> = ({ id, reportValidity, readOnly }) => {
    const classes = useStyles();

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const medicationForm = useInstance(MedicationFormState);
    useSubscription(() => medicationForm);

    const medication = medicationForm.state.medication;

    const checkValidity = () => {
        reportValidity(Boolean(medication?.name));
    };

    useEffect(() => {
        medicationForm.init(id);

        return () => {
            medicationForm.clear();
        };
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        checkValidity();
    }, [medication]); // eslint-disable-line react-hooks/exhaustive-deps

    const onChange = (patch: Partial<IMedication>) => {
        medicationForm.patch({...medication, ...patch});
    };

    return (
        <div data-type="medication-editor" className={classes.form} style={readOnly ? {gap: 30} : {}}>
            <TextField
                name="name"
                readOnly={readOnly}
                value={medication?.name}
                label="Medication Name"
                onInput={(val) => onChange({name: val})}
            />
            <TextArea
                rows={3}
                name="instructions"
                readOnly={readOnly}
                value={medication?.instructions}
                label="Instructions"
                onInput={(val) => onChange({instructions: val})}
            />
            <TextField
                name="dose"
                readOnly={readOnly}
                value={medication?.dose}
                label="Dose"
                onInput={(val) => onChange({dose: val})}
            />
            <TextField
                name="rxNumber"
                readOnly={readOnly}
                value={medication?.rxNumber}
                label="RX #"
                onInput={(val) => onChange({rxNumber: val})}
            />
            <TextField
                name="refills"
                readOnly={readOnly}
                value={medication?.refills}
                label="Refills"
                onInput={(val) => onChange({refills: val})}
            />
            <TextField
                name="expiration"
                readOnly={readOnly}
                value={medication?.expiration}
                label="Expiration Date"
                onInput={(val) => onChange({expiration: val})}
            />
            <TextArea
                autoGrow
                rows={3}
                name="notes"
                readOnly={readOnly}
                value={medication?.notes}
                label="Notes"
                onInput={(val) => onChange({notes: val})}
            />
        </div>
    );
};
